
import {
  TBaseTypographyProps,
  TTypographyAlign,
  TTypographyAlignSubsetProp,
  TTypographyColor,
  TTypographyTag,
  TTypographyWhitespace,
  BASE_TYPOGRAPHY_DEFAULTS,
  TTypographyWeight,
} from "@/ui/components/typography/typography.config";
import { DefinedDefaults, SubsetKeys, subsetOf } from "@/ui/lib/types";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const AVAILABLE_TEXT_BODY_TAGS = subsetOf<TTypographyTag>()([ "p", "address", "strong", "i", "b", "u", "s", "blockquote", "figcaption", "del", "span", "div" ]);

export const AVAILABLE_TEXT_BODY_COLORS =
  subsetOf<TTypographyColor>()([ "default", "inverse", "secondary", "inherit" ]);

export const AVAILABLE_TEXT_BODY_ALIGNMENTS =
  subsetOf<TTypographyAlign>()([ "left", "center", "right" ]);

export const AVAILABLE_TEXT_BODY_WHITESPACE =
  subsetOf<TTypographyWhitespace>()([ "normal", "balance" ]);

export const AVAILABLE_TEXT_BODY_WEIGHTS = subsetOf<TTypographyWeight>()([ "normal", "bold" ]);

export const BASE_TEXT_BODY_SIZE_CLASS_MAP = {
  1: "tracking-sys-tight text-sys-lg leading-sys-1-2",
  2: "tracking-sys-tight text-sys-base leading-sys-1-3",
  3: "text-sys-sm leading-sys-1-3",
  4: "text-sys-xs leading-sys-1-3",
} as const;

export const RESPONSIVE_TEXT_BODY_SIZE_CLASS_MAP = {
  1: `${BASE_TEXT_BODY_SIZE_CLASS_MAP[1]} xl:text-sys-2xl`,
  2: `${BASE_TEXT_BODY_SIZE_CLASS_MAP[2]} xl:text-sys-xl`,
  3: `${BASE_TEXT_BODY_SIZE_CLASS_MAP[3]} xl:text-sys-base`,
  4: `${BASE_TEXT_BODY_SIZE_CLASS_MAP[4]} xl:text-sys-sm`,
} as const;

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TTextBodyTag = SubsetKeys<typeof AVAILABLE_TEXT_BODY_TAGS>;
export type TTextBodyColor = SubsetKeys<typeof AVAILABLE_TEXT_BODY_COLORS>;
export type TTextBodyAlign = SubsetKeys<typeof AVAILABLE_TEXT_BODY_ALIGNMENTS>;
export type TTextBodyWhitespace = SubsetKeys<typeof AVAILABLE_TEXT_BODY_WHITESPACE>;
export type TTextBodySize = keyof typeof BASE_TEXT_BODY_SIZE_CLASS_MAP;
export type TTextBodyWeight = SubsetKeys<typeof AVAILABLE_TEXT_BODY_WEIGHTS>;

export type TTextBodyProps = TBaseTypographyProps & {
  color?: TTextBodyColor;
  align?: TTypographyAlignSubsetProp<TTextBodyAlign>;
  tag?: TTextBodyTag;
  whitespace?: TTextBodyWhitespace;
  size?: TTextBodySize;
  weight?: TTextBodyWeight;
};

type TTextBodyDefaultProps = DefinedDefaults<
  TTextBodyProps,
  "color" | "align" | "tag" | "whitespace" | "size" | "weight"
>;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const TEXT_BODY_DEFAULTS = {
  ...BASE_TYPOGRAPHY_DEFAULTS,
  color: "default",
  align: "left",
  tag: "p",
  whitespace: "normal",
  size: 2,
  weight: "normal",
} satisfies TTextBodyDefaultProps;
